import {
  Button,
  Checkbox,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Spinner,
  Switch,
  Tab,
  Tabs,
} from "@heroui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import {
  Ban,
  CircleCheck,
  ClipboardCheck,
  Download,
  FileText,
  Lock,
  MoreVertical,
  Plus,
  RefreshCw,
  RotateCcw,
  Trash2,
  Undo2,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import AuthorizedButton from "@/components/common/AuthorizedButton";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import DetailField from "@/components/common/DetailField";
import EdgeScrollButton from "@/components/common/EdgeScrollButton";
import PageHeader from "@/components/common/PageHeader";
import SingleFieldEditDialog, {
  type SingleFieldTarget,
} from "@/components/common/SingleFieldEditDialog";
import ColumnSettingsButton from "@/components/table/ColumnSettingsButton";
import DataTable from "@/components/table/DataTable";
import {
  createQuotationItemDrawing,
  uploadDrawingRevision,
} from "@/features/drawings/api";
import { listPartners } from "@/features/partners/api";
import { listUsers, userLabel } from "@/features/users/api";
import { useHorizontalScroll } from "@/hooks/useHorizontalScroll";
import { useTableSettings } from "@/hooks/useTableSettings";
import { errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { canWrite } from "@/lib/authorization";
import { formatAmount } from "@/lib/format";
import { previewQuotationSetProduct } from "@/features/setProduct/api";
import SetProductDialog from "@/features/setProduct/SetProductDialog";
import { showError, showSuccess } from "@/lib/toast";
import {
  cancelQuotationItems,
  cancelQuotationOrder,
  createQuotationItem,
  deleteQuotation,
  deleteQuotationItem,
  deleteQuotationItems,
  getQuotation,
  downloadQuotationAnswer,
  downloadQuotationSheet,
  listQuotationItems,
  QUOTATION_STATUS_LABEL,
  reorderQuotationItems,
  setQuotationSetProduct,
  updateQuotation,
  updateQuotationItem,
  type QuotationInput,
  type QuotationItem,
  type QuotationItemInput,
  type QuotationStatus,
} from "./api";
import {
  buildQuotationItemColumns,
  quotationItemColumnIds,
  quotationItemDefaultHiddenColumnIds,
  quotationItemPinnedColumnIds,
} from "./itemColumns";
import OrderDialog from "./OrderDialog";
import QuotationDrawingsPanel from "./QuotationDrawingsPanel";
import { quotationDrawingsQuery } from "./drawingsQuery";
import ItemDrawingLinkDialog from "./ItemDrawingLinkDialog";
import QuotationItemDialog, { type ItemAttachment } from "./QuotationItemDialog";

/** 詳細画面のタブ。 */
type QuotationDetailTab = "request" | "items" | "drawings";

/** URL に載せられるタブかどうか（知らない値は既定のタブに倒す）。 */
function toTab(value: string | null): QuotationDetailTab {
  return value === "items" || value === "drawings" ? value : "request";
}

/**
 * ヘッダの 1 項目。表示値と、編集ダイアログの設定・保存内容をまとめて持つ。
 *
 * **編集できない項目もある**（起票元の案件など、別の画面へ移るだけのもの）。
 * その場合は `target` / `toInput` を持たず、`linkTo` を持つ（案件詳細と同じ形）。
 */
interface HeaderField {
  key: string;
  label: string;
  display: string | null;
  /** 値を押したときに移る先。入れると編集ではなくリンクになる。 */
  linkTo?: string;
  target?: Omit<SingleFieldTarget, "label">;
  toInput?: (value: string | null) => QuotationInput;
  /** 値の代わりに置く部品（セット品のスイッチ）。一覧と操作を揃えるためのもの。 */
  control?: ReactNode;
}

/**
 * 見積詳細（見積管理画面）。
 *
 * **「見積依頼情報」「明細」「図面」の 3 タブ**で切り替える。依頼項目が 20 を
 * 超えるため、ヘッダと明細を同じ画面に積むと、どちらも中途半端な高さになって
 * しまう。タブなら片方に画面をすべて使える。
 *
 * 図面は明細と別便で届くので、取り込みと突き合わせを独立したタブにしてある
 * （[[QuotationDrawingsPanel]]）。
 *
 * どちらのタブも「青字の値をクリックすると編集ダイアログが開く」操作に揃えている
 * （一覧画面と同じ ngtg_msc 方式）。
 */
export default function QuotationDetail() {
  const { quotationId } = useParams();
  const id = Number(quotationId);
  // 開いているタブは URL に持つ。リロードやブラウザの戻る/進むで元のタブに戻り、
  // 「この見積の明細を見て」と URL で渡せる（一覧の検索条件と同じ方針）。
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: QuotationDetailTab = toTab(searchParams.get("tab"));
  const setTab = (next: QuotationDetailTab) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        // 既定のタブは URL に残さない（共有する URL を無駄に長くしない）。
        if (next === "request") params.delete("tab");
        else params.set("tab", next);
        return params;
      },
      { replace: true },
    );
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const writable = canWrite(user, "transactional");

  const {
    settings,
    setColumnVisibility,
    setColumnOrder,
    setColumnSizing,
    reset: resetColumns,
  } = useTableSettings(
    "quotation-items",
    quotationItemColumnIds,
    quotationItemPinnedColumnIds,
    quotationItemDefaultHiddenColumnIds,
  );
  const { scrollRef, scrollBy, canScrollLeft, canScrollRight } = useHorizontalScroll();

  const [editingField, setEditingField] = useState<HeaderField | null>(null);
  const [addingItem, setAddingItem] = useState(false);
  const [downloadingAnswer, setDownloadingAnswer] = useState(false);
  const [issuingSheet, setIssuingSheet] = useState(false);
  const [deletingItem, setDeletingItem] = useState<QuotationItem | null>(null);
  /**
   * まとめて削除するために選んだ明細の id。
   *
   * 行そのものではなく id で持つ。明細は編集・並べ替えのたびに取り直すので、
   * 行を持つと選択が古い内容を指したままになる。
   */
  const [selectedItemIds, setSelectedItemIds] = useState<Set<number>>(new Set());
  const [bulkDeletingItems, setBulkDeletingItems] = useState(false);
  /**
   * まとめて除外・除外の解除を確認しているか（true なら除外、false なら解除）。
   * 開いていないときは null。
   */
  const [bulkCancellingItems, setBulkCancellingItems] = useState<boolean | null>(null);
  /** 図面の紐付けダイアログを開いている明細（null なら閉じている）。 */
  const [linkingDrawingItem, setLinkingDrawingItem] = useState<QuotationItem | null>(
    null,
  );
  const [deletingQuotation, setDeletingQuotation] = useState(false);
  /** セット品の切り替えの確認ダイアログ（切り替え先。null なら閉じている）。 */
  const [togglingSetProduct, setTogglingSetProduct] = useState<boolean | null>(null);
  const [ordering, setOrdering] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(false);

  const quotationQuery = useQuery({
    queryKey: ["quotation", id],
    queryFn: () => getQuotation(id),
    enabled: Number.isFinite(id),
  });
  const itemsQuery = useQuery({
    queryKey: ["quotation-items", id],
    queryFn: () => listQuotationItems(id),
    enabled: Number.isFinite(id),
  });
  // タブに件数を出すためだけに引く。図面タブ（[[QuotationDrawingsPanel]]）と
  // 同じキーなので、両方が表示されていても問い合わせは 1 回で済む。
  const drawingsQuery = useQuery({
    ...quotationDrawingsQuery(id),
    enabled: Number.isFinite(id),
  });
  // 得意先は取引中のものだけを選択肢に出す（取引を止めた相手を選ばせない）。
  const partnersQuery = useQuery({
    queryKey: ["partners", "picker"],
    queryFn: () =>
      listPartners({ disabled: false, isClient: true, limit: 500, offset: 0 }),
  });
  const usersQuery = useQuery({
    queryKey: ["users", "picker"],
    queryFn: () => listUsers(),
  });

  const quotation = quotationQuery.data;
  const items = itemsQuery.data?.items ?? [];
  /** すでに受注済みか。案件の二重起票を防ぐために受注のボタンを閉じる。 */
  const ordered = quotation?.status === "ordered";
  /**
   * 中身を直せるか。**受注済みの見積は直せない**。
   *
   * 受注すると明細は案件へ写される（[[OrderDialog]]）。写したあとに見積の側を
   * 直しても案件は追従しないので、**手元の見積と現場が見ている案件が食い違う**。
   * 見積は「何をいくらで約束したか」の控えでもあるので、後から書き換えられない
   * ほうがよい。直す必要があるときは受注を取り消してからにする。
   *
   * 権限そのもの（`writable`）とは分けて持つ。受注の取り消しと削除は受注済みでも
   * できなければならず、そちらは権限だけで決まる。
   */
  const editable = writable && !ordered;

  /**
   * セット品の切り替えで**明細の形まで変わる**見積か（購番の行を持つか）。
   *
   * 紙の行があるときは畳み込み・解除になるので確認ダイアログを通し、案件の明細
   * も一緒に作り直す（[[set_product_fold]]）。無いときは印とセット数だけが動く。
   */
  const foldsSetProduct = (quotation?.client_order_numbers.length ?? 0) > 0;

  /**
   * セット品の印を切り替える。行き先は紙の行があるかで変わる
   * （[[foldsSetProduct]]）。
   */
  const toggleSetProduct = async (to: boolean) => {
    if (foldsSetProduct) {
      setTogglingSetProduct(to);
      return;
    }
    try {
      await updateQuotation(id, { is_set_product: to });
    } catch (e) {
      showError(errorMessage(e, "セット品の切り替えに失敗しました。"));
      return;
    }
    await invalidate();
    showSuccess(to ? "セット品にしました。" : "セット品を解除しました。");
  };

  /** ヘッダと明細の両方を引き直す（明細を変えると合計と明細行数も変わる）。 */
  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["quotation", id] }),
      queryClient.invalidateQueries({ queryKey: ["quotation-items", id] }),
      queryClient.invalidateQueries({ queryKey: ["quotations"] }),
    ]);
  };

  /**
   * ヘッダの項目。タブに分けたので畳まず全部出す。
   *
   * main / request は表示順のまとまりでしかない（見積を組むのに要るものを先に、
   * 客先の依頼内容を後に）。2 列に流し込むので、関係の近い項目が隣り合うように
   * 並べてある（備考と社内備考など）。
   */
  const headerFields = useMemo<HeaderField[]>(() => {
    if (!quotation) return [];
    const partners = partnersQuery.data?.items ?? [];
    const users = usersQuery.data?.items ?? [];
    const userName = (userId: string | null) =>
      users.find((u) => u.user_id === userId)?.name ?? null;

    const date = (
      key: string,
      label: string,
      value: string | null,
      field: keyof QuotationInput,
    ): HeaderField => ({
      key,
      label,
      display: value,
      target: { value, kind: "date" },
      toInput: (v) => ({ [field]: v }) as QuotationInput,
    });

    const text = (
      key: string,
      label: string,
      value: string | null,
      field: keyof QuotationInput,
      kind: "text" | "textarea" = "text",
    ): HeaderField => ({
      key,
      label,
      display: value,
      target: { value, kind },
      toInput: (v) => ({ [field]: v }) as QuotationInput,
    });

    const main: HeaderField[] = [
      // 特急フロー（案件が先、見積が後）で起こした見積にだけ出す。この見積を
      // 受注にすると、案件を新しく作らずこの案件に結び付く
      // （[[urgent-project-flow]]）ので、押す前に相手が見えている必要がある。
      ...(quotation.origin_project_id == null
        ? []
        : [
            {
              key: "origin_project",
              label: "起票元の案件",
              display: `${quotation.origin_project_number}（特急）`,
              linkTo: `/projects/${quotation.origin_project_id}`,
            },
          ]),
      {
        key: "client",
        label: "得意先",
        display: quotation.client_name,
        target: {
          value: quotation.client_id != null ? String(quotation.client_id) : null,
          kind: "select",
          options: partners.map((p) => ({
            key: String(p.partner_id),
            label: `${p.partner_code} ${p.partner_name}`,
          })),
        },
        toInput: (v) => ({ client_id: v == null ? null : Number(v) }),
      },
      {
        key: "status",
        label: "状態",
        display: QUOTATION_STATUS_LABEL[quotation.status],
        target: {
          value: quotation.status,
          kind: "select",
          required: true,
          options: Object.entries(QUOTATION_STATUS_LABEL).map(([key, label]) => ({
            key,
            label,
          })),
          description: "失注にする場合は先に失注理由を入力してください。",
        },
        toInput: (v) => ({ status: (v ?? undefined) as QuotationStatus }),
      },
      text(
        "client_request_number",
        "客先依頼番号",
        quotation.client_request_number,
        "client_request_number",
      ),
      text("subject", "件名", quotation.subject, "subject"),
      {
        key: "is_urgent",
        label: "特急",
        display: quotation.is_urgent ? "特急" : "通常",
        target: {
          value: quotation.is_urgent ? "true" : "false",
          kind: "select",
          required: true,
          options: [
            { key: "false", label: "通常" },
            { key: "true", label: "特急" },
          ],
          description: "客先の緊急区分です。状態とは別に立ちます。",
        },
        toInput: (v) => ({ is_urgent: v === "true" }),
      },
      {
        // セット品であることは紙から決まり、**受注のとき案件へ写る**。見た目と
        // 操作は案件詳細・一覧の列と揃えてスイッチにする（[[ProjectDetail]]）。
        //
        // 押したときの行き先は紙の行があるかで変わる。購番の行が付いている見積
        // は**見積と案件の明細の形まで変わる**ので確認ダイアログを通し、紙の行が
        // 無い見積は印とセット数だけをその場で直す。
        key: "is_set_product",
        label: "セット品",
        display: quotation.is_set_product ? "セット品" : null,
        control: (
          <Switch
            size="sm"
            isSelected={quotation.is_set_product}
            // 畳み込みの側は受注済みでも押せる（案件と一緒に作り直すため）。
            isDisabled={foldsSetProduct ? !writable : !editable}
            onValueChange={() => toggleSetProduct(!quotation.is_set_product)}
            aria-label="セット品"
            classNames={{ base: "m-0 p-0", label: "hidden" }}
          />
        ),
      },
      {
        key: "set_count",
        label: "セット数",
        display: quotation.set_count == null ? null : `${quotation.set_count} セット`,
        // 購番の行が付いている見積のセット数は**購番の本数から決まる導出値**
        // なので直せない（API も 409 で断る）。紙の行の無い見積だけ手で入れられる。
        target: foldsSetProduct
          ? undefined
          : {
              value: quotation.set_count == null ? null : String(quotation.set_count),
              kind: "number",
              description:
                "何セット作るか。入れるとセット品になります。明細の数量は「1 セットあたり × セット数」の合算です。",
            },
        toInput: (v) => ({ set_count: v == null || v === "" ? null : Number(v) }),
      },
      {
        // 畳む前の購番（[[client_order_lines]]）。客先依頼番号は 1 本しか持てない
        // ので、セット品の購番はここに並ぶ。**直せない**（紙の行の記録）。
        key: "client_order_numbers",
        label: "客先購番",
        display:
          quotation.client_order_numbers.length === 0
            ? null
            : quotation.client_order_numbers.join(" / "),
      },
      {
        key: "sales_rep",
        label: "営業担当",
        display: userName(quotation.sales_rep_id),
        target: {
          value: quotation.sales_rep_id,
          kind: "select",
          options: users.map((u) => ({ key: u.user_id, label: userLabel(u) })),
        },
        toInput: (v) => ({ sales_rep_id: v }),
      },
      {
        key: "quoted_by",
        label: "見積担当",
        display: userName(quotation.quoted_by),
        target: {
          value: quotation.quoted_by,
          kind: "select",
          options: users.map((u) => ({ key: u.user_id, label: userLabel(u) })),
        },
        toInput: (v) => ({ quoted_by: v }),
      },
      date("requested_at", "依頼受領日", quotation.requested_at, "requested_at"),
      date("answer_due_date", "回答期限", quotation.answer_due_date, "answer_due_date"),
      date("quoted_date", "見積日", quotation.quoted_date, "quoted_date"),
      date("valid_until", "有効期限", quotation.valid_until, "valid_until"),
      {
        key: "lost_reason",
        label: "失注・辞退の理由",
        display: quotation.lost_reason,
        target: { value: quotation.lost_reason, kind: "textarea" },
        toInput: (v) => ({ lost_reason: v }),
      },
    ];

    const request: HeaderField[] = [
      date(
        "client_request_date",
        "客先依頼日",
        quotation.client_request_date,
        "client_request_date",
      ),
      text("ship_to", "納入先", quotation.ship_to, "ship_to"),
      text(
        "client_department",
        "依頼部門",
        quotation.client_department,
        "client_department",
      ),
      text(
        "client_contact_name",
        "客先担当",
        quotation.client_contact_name,
        "client_contact_name",
      ),
      date(
        "material_supply_date",
        "支給日",
        quotation.material_supply_date,
        "material_supply_date",
      ),
      {
        key: "currency",
        label: "通貨",
        display: quotation.currency,
        target: {
          value: quotation.currency,
          kind: "text",
          required: true,
          description: "ISO 4217（現状は JPY のみ）。",
        },
        toInput: (v) => ({ currency: v ?? "JPY" }),
      },
      text("payment_terms", "支払条件", quotation.payment_terms, "payment_terms"),
      {
        key: "note",
        label: "備考",
        display: quotation.note,
        target: {
          value: quotation.note,
          kind: "textarea",
          description: "見積書に載ります。社内だけのメモは「社内備考」へ。",
        },
        toInput: (v) => ({ note: v }),
      },
      {
        key: "internal_note",
        label: "社内備考",
        display: quotation.internal_note,
        target: {
          value: quotation.internal_note,
          kind: "textarea",
          description: "客先には出ません。見積書にも載りません。",
        },
        toInput: (v) => ({ internal_note: v }),
      },
    ];

    return [...main, ...request];
    // editable / writable / foldsSetProduct はセット品のスイッチを押せるか
    // どうかに効く（権限や紙の行が変われば項目も組み直す）。toggleSetProduct は
    // 毎回作り直されるが、中身はこの 3 つと見積にしか依存しない。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quotation,
    partnersQuery.data,
    usersQuery.data,
    editable,
    writable,
    foldsSetProduct,
  ]);

  const saveHeaderField = async (value: string | null): Promise<string | null> => {
    if (!editingField) return null;
    try {
      if (!editingField.toInput) return null;
      await updateQuotation(id, editingField.toInput(value));
      await invalidate();
      return null;
    } catch (e) {
      // 入力し直せば解決する失敗（失注理由なしの失注など）はダイアログ内に返す。
      return errorMessage(e, "保存に失敗しました。");
    }
  };

  /**
   * 明細を保存し、PDF が添付されていれば続けてアップロードする。
   *
   * PDF は図面 ID が決まってからでないと上げられないので、必ず明細の保存が先。
   * 明細の保存に失敗したときは何も書かれていないのでダイアログに文言を返し、
   * 入力し直してもらう。**PDF のアップロードだけ失敗したときはダイアログを
   * 閉じてトーストで知らせる**。ここで開いたままにすると、再送信で明細が
   * もう 1 行できてしまうため（明細は保存済み。PDF は開き直して添付できる）。
   */
  const submitItem = async (
    input: QuotationItemInput,
    attachment: ItemAttachment | null,
  ): Promise<string | null> => {
    let saved: QuotationItem;
    try {
      saved = await createQuotationItem(id, input);
    } catch (e) {
      return errorMessage(e, "保存に失敗しました。");
    }

    if (attachment) {
      try {
        // **明細を足しただけでは図面の器は作られない**（中身の無い図面行が増える
        // だけなので、PDF を上げるときに作る方針）。`saved.drawing_id` は必ず
        // null なので、ここで器を作ってから PDF を載せる。
        const drawing = await createQuotationItemDrawing(id, saved.quotation_item_id);
        // 版はサーバー側で採番する。登録するとサムネイルと図番の読み取り（OCR）が
        // 応答のあとに走る。明細との紐付けは器を作った時点で済んでいる。
        await uploadDrawingRevision(drawing.drawing_id, attachment.file);
      } catch (e) {
        showError(
          `明細は保存しましたが、PDF を登録できませんでした: ${errorMessage(e, "原因不明のエラー")}`,
        );
      }
    }

    await invalidate();
    return null;
  };

  /** 一覧のセルから 1 項目だけ保存する（数量・単価・摘要など）。 */
  const saveItemField = async (
    item: QuotationItem,
    input: QuotationItemInput,
  ): Promise<string | null> => {
    try {
      await updateQuotationItem(id, item.quotation_item_id, input);
      await invalidate();
      return null;
    } catch (e) {
      return errorMessage(e, "保存に失敗しました。");
    }
  };

  const toggleCompleted = useMutation({
    mutationFn: (item: QuotationItem) =>
      updateQuotationItem(id, item.quotation_item_id, {
        completed: !item.completed,
      }),
    onSuccess: () => void invalidate(),
    onError: (e: Error) => showError(e.message),
  });

  /**
   * 除外（見積から外す）の切り替え。1 行ぶんもまとめてぶんも同じ API を使う。
   *
   * 客先が明細の一部だけを発注してきたときに使う。行は消さずに残すので、何を
   * 見積って何が落ちたのかが後から辿れる。案件に紐付いた行は API が 409 で断る。
   */
  const toggleCancelled = useMutation({
    mutationFn: ({ ids, cancelled }: { ids: number[]; cancelled: boolean }) =>
      cancelQuotationItems(id, ids, cancelled),
    onSuccess: async (_data, { ids, cancelled }) => {
      await invalidate();
      setSelectedItemIds(new Set());
      showSuccess(
        cancelled
          ? `${ids.length} 行を見積から除外しました。`
          : `${ids.length} 行の除外を解除しました。`,
      );
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setBulkCancellingItems(null),
  });

  const reorder = useMutation({
    mutationFn: (ids: number[]) => reorderQuotationItems(id, ids),
    onSuccess: () => void invalidate(),
    onError: (e: Error) => showError(e.message),
  });

  /** 掴んだ行を、落とした行の位置へ移す（先頭列のハンドルでのドラッグ）。 */
  const moveTo = (draggedId: number, targetId: number) => {
    const ids = items.map((i) => i.quotation_item_id);
    const from = ids.indexOf(draggedId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0 || from === to) return;
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved);
    reorder.mutate(ids);
  };

  /** 1 行ぶん隣と入れ替える（ハンドルでの ↑↓ キー操作）。 */
  const moveBy = (item: QuotationItem, direction: -1 | 1) => {
    const ids = items.map((i) => i.quotation_item_id);
    const from = ids.indexOf(item.quotation_item_id);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= ids.length) return;
    [ids[from], ids[to]] = [ids[to], ids[from]];
    reorder.mutate(ids);
  };

  const removeItem = useMutation({
    mutationFn: (item: QuotationItem) =>
      deleteQuotationItem(id, item.quotation_item_id),
    onSuccess: (_, item) => {
      showSuccess(`${item.sequence} 行目を削除しました。`);
      void invalidate();
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setDeletingItem(null),
  });

  /**
   * 選ばれている明細。**毎回 items から引き直す**ので、消えた行の id が選択に
   * 残っていても無視される。
   */
  const selectedItems = items.filter((i) => selectedItemIds.has(i.quotation_item_id));
  const allItemsSelected = items.length > 0 && selectedItems.length === items.length;
  /** 除外した明細（合計金額と進捗の集計から外れている行）。 */
  const cancelledItems = items.filter((i) => i.cancelled);
  // 除外と解除は、選択の中に対象がある側だけボタンを出す（押しても何も
  // 変わらないボタンを並べない）。
  const selectedToCancel = selectedItems.filter((i) => !i.cancelled);
  const selectedToRestore = selectedItems.filter((i) => i.cancelled);

  const toggleSelectItem = (item: QuotationItem) =>
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(item.quotation_item_id)) next.delete(item.quotation_item_id);
      else next.add(item.quotation_item_id);
      return next;
    });

  const toggleSelectAllItems = () =>
    setSelectedItemIds(
      allItemsSelected ? new Set() : new Set(items.map((i) => i.quotation_item_id)),
    );

  const removeItems = useMutation({
    mutationFn: (ids: number[]) => deleteQuotationItems(id, ids),
    onSuccess: async (_data, ids) => {
      await invalidate();
      setSelectedItemIds(new Set());
      showSuccess(`${ids.length} 行を削除しました。`);
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setBulkDeletingItems(false),
  });

  const removeQuotation = useMutation({
    mutationFn: () => deleteQuotation(id),
    onSuccess: () => {
      showSuccess(`見積 ${quotation?.quotation_number} を削除しました。`);
      void queryClient.invalidateQueries({ queryKey: ["quotations"] });
      navigate("/quotations");
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setDeletingQuotation(false),
  });

  /**
   * 受注を取り消す。起票した案件も一緒に消える。
   *
   * 消えるのは案件側だけで、見積の明細も図面もそのまま残る。取り消したあとは
   * もう一度「受注にする」が押せる。
   *
   * **特急フロー（案件が先）では案件を消さない**。案件は見積より先に実在し、
   * 製作も進んでいるので、外れるのは紐付けだけ（[[urgent-project-flow]]）。
   */
  const cancelOrder = useMutation({
    mutationFn: () => cancelQuotationOrder(id),
    onSuccess: (updated) => {
      showSuccess(
        // 特急フロー（案件が先）では案件を消さない。消したと言うと、現場が
        // 動いている案件が消えたと読めてしまう（[[urgent-project-flow]]）。
        quotation?.origin_project_number
          ? `受注を取り消し、案件 ${quotation.origin_project_number} との結び付けを外しました。`
          : quotation?.ordered_project_number
            ? `受注を取り消し、案件 ${quotation.ordered_project_number} を削除しました。`
            : "受注を取り消しました。",
      );
      queryClient.setQueryData(["quotation", id], updated);
      void invalidate();
      // 案件が 1 件消えるので、案件一覧の取り置きも捨てる。
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
    onError: (e: Error) => showError(e.message),
    onSettled: () => setCancellingOrder(false),
  });

  /** 回答ファイルを書き出す。様式が無い得意先などは API が理由を返す。 */
  const downloadAnswer = async () => {
    setDownloadingAnswer(true);
    try {
      await downloadQuotationAnswer(id);
    } catch (e) {
      showError(errorMessage(e, "回答ファイルを書き出せませんでした。"));
    } finally {
      setDownloadingAnswer(false);
    }
  };

  /**
   * 見積書 PDF を保存する。
   *
   * 出せない見積（得意先が未設定／明細が無い／工程を入れていない図面行がある）は
   * API が 400 で止めるので、その理由をそのまま出す。0 円の行が混じった紙を
   * 客先に出すと過少見積になり、気づくのは受注して原価が出てからになるため
   * （[[quotation_sheet_service]]）。
   */
  const issueSheet = async () => {
    setIssuingSheet(true);
    try {
      await downloadQuotationSheet(id);
    } catch (e) {
      showError(errorMessage(e, "見積書を発行できませんでした。"));
    } finally {
      setIssuingSheet(false);
    }
  };

  const drawingCount = drawingsQuery.data?.items.length ?? 0;
  const unmatchedDrawings = drawingsQuery.data?.unmatched_count ?? 0;

  const columns = useMemo(
    () =>
      buildQuotationItemColumns({
        canWrite: editable,
        // 図面だけは受注済みでも足せる（受注後に届く追図・差し替えのため）。
        canLinkDrawing: writable,
        itemCount: items.length,
        selectedIds: selectedItemIds,
        onToggleSelect: toggleSelectItem,
        onOpenEditor: (item) =>
          navigate(`/quotations/${id}/items/${item.quotation_item_id}`),
        onOpenDrawingLink: setLinkingDrawingItem,
        onSaveField: saveItemField,
        onToggleCompleted: (item) => toggleCompleted.mutate(item),
        onToggleCancelled: (item) =>
          toggleCancelled.mutate({
            ids: [item.quotation_item_id],
            cancelled: !item.cancelled,
          }),
        onMove: moveBy,
        onDelete: setDeletingItem,
      }),
    // move は mutate の同一性が保たれないため依存に入れない（items が変われば作り直る）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editable, writable, items.length, selectedItemIds],
  );

  const table = useReactTable<QuotationItem>({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.quotation_item_id),
    columnResizeMode: "onChange",
    // 並び順は見積書の行番号そのもの。列ヘッダでの並び替えは持たない。
    enableSorting: false,
    state: {
      columnVisibility: settings.columnVisibility,
      columnOrder: settings.columnOrder,
      columnSizing: settings.columnSizing,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    defaultColumn: { minSize: 60, maxSize: 600 },
  });

  if (quotationQuery.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner label="読み込み中..." />
      </div>
    );
  }

  if (quotationQuery.isError || !quotation) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <p className="text-default-600">
          見積を取得できませんでした。
          <br />
          {(quotationQuery.error as Error | null)?.message}
        </p>
        <Button color="primary" onPress={() => navigate("/quotations")}>
          見積一覧へ戻る
        </Button>
      </div>
    );
  }

  return (
    <div className="relative h-full">
      <div className="flex h-full flex-col px-6 pt-3 pb-3">
        <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-white shadow-md">
          <PageHeader
            title={quotation.quotation_number}
            parents={[{ label: "見積管理", to: "/quotations" }]}
            actions={
              <>
                {/* 通常フローの本線。手で写させないのは、写し間違いを防ぐためと、
                    見積との紐付けが埋まらないまま案件が進むのを防ぐため。
                    受注済みは二重に案件を起票できないよう無効にし、ボタンの場所を
                    そのまま「受注済」の表示に使う（消すと受注したのかどうかが
                    画面の上で分からなくなる）。 */}
                <AuthorizedButton
                  scope="transactional"
                  radius="full"
                  color={ordered ? "success" : "primary"}
                  variant={ordered ? "flat" : "solid"}
                  isDisabled={ordered}
                  // 無効のままだと既定の薄さで「受注済」が読み取りにくい。ここは
                  // 押せないことより状態を伝えることが目的なので、色は戻す。
                  className={ordered ? "cursor-default !opacity-100" : undefined}
                  data-testid="order-open"
                  startContent={
                    ordered ? <CircleCheck size={18} /> : <ClipboardCheck size={18} />
                  }
                  onPress={() => setOrdering(true)}
                >
                  {ordered
                    ? "受注済"
                    : quotation.origin_project_number == null
                      ? "受注にする"
                      : `受注にする（${quotation.origin_project_number} に結び付ける）`}
                </AuthorizedButton>
                {/* 回答ファイルの書き出し。これまで手で表計算に打ち直して
                    いた作業なので、見積を見ている画面から直接出せるようにする。 */}
                <Button
                  radius="full"
                  variant="bordered"
                  data-testid="download-answer"
                  startContent={<Download size={18} />}
                  isLoading={downloadingAnswer}
                  onPress={() => void downloadAnswer()}
                >
                  回答ファイル
                </Button>
                <Dropdown>
                  <DropdownTrigger>
                    <Button isIconOnly variant="light" radius="full" aria-label="操作">
                      <MoreVertical size={20} className="text-brand-500" />
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu
                    aria-label="操作"
                    onAction={(key) => {
                      if (key === "reload") void invalidate();
                      if (key === "sheet") void issueSheet();
                      if (key === "cancel-order") setCancellingOrder(true);
                      if (key === "delete") setDeletingQuotation(true);
                    }}
                    // 受注取り消しは受注済みのときだけ意味がある。項目を消さずに
                    // 無効で出すのは、「受注を戻す手段がある」ことを受注前から
                    // 知っておけるようにするため。
                    disabledKeys={[
                      ...(writable ? [] : ["delete", "cancel-order"]),
                      ...(ordered ? [] : ["cancel-order"]),
                    ]}
                  >
                    <DropdownItem key="reload" startContent={<RefreshCw size={18} />}>
                      再読み込み
                    </DropdownItem>
                    {/* 見積書はこの見積 1 件ぶんを 1 通で出す。得意先・明細・
                        工程が欠けている見積は API が止めて理由を返す。 */}
                    <DropdownItem
                      key="sheet"
                      data-testid="issue-sheet"
                      startContent={<FileText size={18} />}
                      description="この見積の明細から PDF を作ります"
                    >
                      {issuingSheet ? "見積書を発行中…" : "見積書発行"}
                    </DropdownItem>
                    <DropdownItem
                      key="cancel-order"
                      startContent={<Undo2 size={18} />}
                      description="案件を削除して提出済に戻す"
                    >
                      受注を取り消す
                    </DropdownItem>
                    <DropdownItem
                      key="delete"
                      className="text-danger"
                      color="danger"
                      startContent={<Trash2 size={18} />}
                    >
                      この見積を削除
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </>
            }
          />
          <div className="border-b border-default-200 px-6">
            <Tabs
              aria-label="見積の表示切り替え"
              selectedKey={tab}
              onSelectionChange={(key) => setTab(key as QuotationDetailTab)}
              variant="underlined"
              color="primary"
              classNames={{ tabList: "gap-6 p-0", tab: "h-11 px-0" }}
            >
              <Tab key="request" title="見積依頼情報" />
              <Tab
                key="items"
                title={
                  <span className="flex items-center gap-1.5">
                    明細
                    <span className="rounded-full bg-default-100 px-2 text-tiny text-default-600">
                      {items.length}
                    </span>
                  </span>
                }
              />
              <Tab
                key="drawings"
                title={
                  <span className="flex items-center gap-1.5">
                    図面
                    <span className="rounded-full bg-default-100 px-2 text-tiny text-default-600">
                      {drawingCount}
                    </span>
                    {/* 紐付無しはこのタブでやる仕事そのものなので、件数とは別に
                        目立たせる。0 件のときは出さない。 */}
                    {unmatchedDrawings > 0 && (
                      <span className="rounded-full bg-warning-100 px-2 text-tiny font-bold text-warning-700">
                        紐付無し {unmatchedDrawings}
                      </span>
                    )}
                  </span>
                }
              />
            </Tabs>
          </div>

          {/* **直せない理由はその場に書く。** 鉛筆が消えるだけだと、権限が無い
              のか、受注して締まったのかが読み取れない。戻し方（受注の取り消し）
              まで書いておかないと、直したい人はメニューを探し回ることになる。 */}
          {ordered && (
            <div className="flex items-center gap-2 border-b border-default-200 bg-default-50 px-6 py-2 text-sm text-default-600">
              <Lock size={14} className="shrink-0 text-default-400" />
              <span>
                受注済みのため、依頼情報・明細・工程は編集できません（図面の追加と紐付けはできます）。直すときは、右上のメニューから受注を取り消してください（案件{" "}
                {quotation.ordered_project_number} も削除されます）。
              </span>
            </div>
          )}

          {tab === "request" ? (
            /* 依頼情報は畳まずに全項目を出す。項目数が多いので縦に流す。 */
            <div className="min-h-0 flex-1 overflow-auto px-6 py-2">
              <div className="grid grid-cols-1 gap-x-8 lg:grid-cols-2">
                {headerFields.map((field) => (
                  <DetailField
                    key={field.key}
                    label={field.label}
                    value={field.display}
                    canWrite={editable && field.target != null}
                    linkTo={field.linkTo}
                    onEdit={() => setEditingField(field)}
                  />
                ))}
              </div>
            </div>
          ) : tab === "drawings" ? (
            <QuotationDrawingsPanel
              quotationId={id}
              // 図面タブは受注済みでも開けておく（[[drawing_project_link]]）。
              canWrite={writable}
              // 紐付け先の候補は製作対象の行だけ（運搬費・値引きには付かない）。
              items={items.filter((i) => i.drawing_number != null)}
            />
          ) : (
            <>
              <div className="flex min-h-9 flex-wrap items-center gap-x-2.5 gap-y-2 px-6 py-3">
                <span className="text-sm text-default-500">
                  {items.length} 行
                  {/* 合計金額は除外した行を含まないので、何行外しているかを
                      金額の隣に出す（出さないと、合計が合わないように見える）。 */}
                  {cancelledItems.length > 0 && (
                    <span className="ml-1">（除外 {cancelledItems.length} 行）</span>
                  )}{" "}
                  / 合計{" "}
                  <span className="font-bold text-default-700">
                    {formatAmount(itemsQuery.data?.total_amount ?? "0")} 円
                  </span>
                  {/* 選んだ件数は、削除が何行に効くかを示す。 */}
                  {selectedItems.length > 0 && (
                    <span className="ml-2 font-bold text-brand-600">
                      {selectedItems.length} 行を選択中
                    </span>
                  )}
                </span>
                {/* 全行の選択はここで 1 回押せば済む（表のヘッダは並び替えの
                    ボタンで埋まっており、中にチェックボックスを置けない）。 */}
                {editable && items.length > 0 && (
                  <Checkbox
                    size="sm"
                    isSelected={allItemsSelected}
                    isIndeterminate={selectedItems.length > 0 && !allItemsSelected}
                    onValueChange={toggleSelectAllItems}
                    data-testid="item-select-all"
                  >
                    <span className="text-sm">すべて選択</span>
                  </Checkbox>
                )}
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  {/* **削除は選んでから**。常に出しておくと、押し間違えたときに
                      何行消えるのかが画面から読めない。 */}
                  {/* **除外は削除の手前の選択肢**。客先が一部だけ発注してきた
                      ときは行を消さずに外す（何を見積って何が落ちたのかが
                      後から辿れる）ので、削除より先に並べる。 */}
                  {selectedToCancel.length > 0 && (
                    <AuthorizedButton
                      scope="transactional"
                      radius="full"
                      variant="light"
                      data-testid="item-bulk-cancel"
                      startContent={<Ban size={18} />}
                      isLoading={toggleCancelled.isPending}
                      onPress={() => setBulkCancellingItems(true)}
                    >
                      選んだ {selectedToCancel.length} 行を除外
                    </AuthorizedButton>
                  )}
                  {selectedToRestore.length > 0 && (
                    <AuthorizedButton
                      scope="transactional"
                      radius="full"
                      variant="light"
                      data-testid="item-bulk-restore"
                      startContent={<RotateCcw size={18} />}
                      isLoading={toggleCancelled.isPending}
                      onPress={() => setBulkCancellingItems(false)}
                    >
                      除外を解除（{selectedToRestore.length} 行）
                    </AuthorizedButton>
                  )}
                  {selectedItems.length > 0 && (
                    <AuthorizedButton
                      scope="transactional"
                      radius="full"
                      variant="light"
                      color="danger"
                      data-testid="item-bulk-delete"
                      startContent={<Trash2 size={18} />}
                      isLoading={removeItems.isPending}
                      onPress={() => setBulkDeletingItems(true)}
                    >
                      選んだ {selectedItems.length} 行を削除
                    </AuthorizedButton>
                  )}
                  <AuthorizedButton
                    scope="transactional"
                    radius="full"
                    color="primary"
                    data-testid="item-create"
                    startContent={<Plus size={18} />}
                    // 受注済みは行を足せない（理由はタブの下に出している）。
                    isDisabled={!editable}
                    onPress={() => setAddingItem(true)}
                  >
                    明細を追加
                  </AuthorizedButton>
                  <ColumnSettingsButton table={table} onReset={resetColumns} />
                </div>
              </div>

              <div className="min-h-0 flex-1">
                {itemsQuery.isError ? (
                  <div className="flex h-full flex-col items-center justify-center gap-4">
                    <p className="text-default-600">
                      明細を取得できませんでした。
                      <br />
                      {(itemsQuery.error as Error).message}
                    </p>
                    <Button color="primary" onPress={() => void itemsQuery.refetch()}>
                      再読み込み
                    </Button>
                  </div>
                ) : itemsQuery.isPending ? (
                  <div className="flex h-full items-center justify-center">
                    <Spinner label="読み込み中..." />
                  </div>
                ) : (
                  <DataTable
                    table={table}
                    isLoading={itemsQuery.isFetching}
                    emptyMessage="明細がありません。「明細を追加」から図面を登録してください。"
                    scrollRef={scrollRef}
                    onReorderRow={(draggedId, targetId) =>
                      moveTo(Number(draggedId), Number(targetId))
                    }
                    // 除外した行はグレーにして、縞模様より目立たせる。セルは
                    // 列定義側で編集不可になっているので、ここは見た目だけ。
                    rowAppearance={(item) =>
                      item.cancelled
                        ? { background: "bg-default-100", text: "text-default-400" }
                        : undefined
                    }
                  />
                )}
              </div>
            </>
          )}
        </section>
      </div>

      {/* 横スクロールは明細の表にだけ要る。 */}
      {tab === "items" && (
        <>
          <EdgeScrollButton
            side="left"
            isDisabled={!canScrollLeft}
            onPress={() => scrollBy(-1)}
          />
          <EdgeScrollButton
            side="right"
            isDisabled={!canScrollRight}
            onPress={() => scrollBy(1)}
          />
        </>
      )}

      <SingleFieldEditDialog
        target={
          editingField?.target
            ? { label: editingField.label, ...editingField.target }
            : null
        }
        onClose={() => setEditingField(null)}
        onSave={saveHeaderField}
      />

      <QuotationItemDialog
        isOpen={addingItem}
        onClose={() => setAddingItem(false)}
        onSubmit={submitItem}
      />

      <OrderDialog
        isOpen={ordering}
        quotationId={id}
        quotationNumber={quotation.quotation_number}
        itemCount={items.filter((i) => i.drawing_number != null && !i.cancelled).length}
        originProjectNumber={quotation.origin_project_number}
        onClose={() => setOrdering(false)}
        onCreated={(project) => {
          showSuccess(
            quotation.origin_project_id == null
              ? `案件 ${project.project_number} を起票しました。`
              : `案件 ${project.project_number} に結び付けました。`,
          );
          void invalidate();
          navigate(`/projects/${project.project_id}`);
        }}
      />

      <ConfirmDialog
        isOpen={deletingItem != null}
        title="明細の削除"
        message={`${deletingItem?.sequence} 行目「${deletingItem?.item_name}」を削除します。\nこの操作は取り消せません。よろしいですか？\n\n行番号は詰めません。並べ直す場合は先頭列のハンドルをドラッグしてください。`}
        confirmLabel="削除"
        isDanger
        isBusy={removeItem.isPending}
        onClose={() => setDeletingItem(null)}
        onConfirm={() => {
          if (deletingItem) removeItem.mutate(deletingItem);
        }}
      />

      {/* 明細の「図面リンク」から開く。図面タブと向きが逆（行 → 図面）の口。 */}
      <ItemDrawingLinkDialog
        quotationId={id}
        item={linkingDrawingItem}
        canWrite={writable}
        onClose={() => setLinkingDrawingItem(null)}
      />

      <ConfirmDialog
        isOpen={bulkDeletingItems}
        title="選んだ明細の削除"
        message={`選んだ ${selectedItems.length} 行を削除します。\nこの操作は取り消せません。よろしいですか？\n\n案件に紐付いている行が混ざっていると、1 行も削除しません。\n行番号は詰めません。`}
        confirmLabel="削除"
        isDanger
        isBusy={removeItems.isPending}
        onClose={() => setBulkDeletingItems(false)}
        onConfirm={() =>
          removeItems.mutate(selectedItems.map((i) => i.quotation_item_id))
        }
      />

      {/* 除外は取り消せる（解除できる）ので、削除ほど強い確認にはしない。
          ただし合計金額と受注に効くので、何行に効くのかは必ず見せる。 */}
      <ConfirmDialog
        isOpen={bulkCancellingItems != null}
        title={bulkCancellingItems ? "選んだ明細の除外" : "除外の解除"}
        message={
          bulkCancellingItems
            ? `選んだ ${selectedToCancel.length} 行を見積から除外します。\n\n除外した行は合計金額と進捗の集計から外れ、受注しても案件には入りません。行は残るので、あとで解除できます。\n\n案件に紐付いている行が混ざっていると、1 行も除外しません。`
            : `選んだ ${selectedToRestore.length} 行の除外を解除し、見積に戻します。\n\n解除した行は合計金額に戻り、また編集できるようになります。`
        }
        confirmLabel={bulkCancellingItems ? "除外する" : "解除する"}
        isBusy={toggleCancelled.isPending}
        onClose={() => setBulkCancellingItems(null)}
        onConfirm={() =>
          toggleCancelled.mutate({
            ids: (bulkCancellingItems ? selectedToCancel : selectedToRestore).map(
              (i) => i.quotation_item_id,
            ),
            cancelled: bulkCancellingItems === true,
          })
        }
      />

      <ConfirmDialog
        isOpen={cancellingOrder}
        title="受注の取り消し"
        message={`見積 ${quotation.quotation_number} の受注を取り消します。\nこの操作は取り消せません。よろしいですか？\n\n${
          quotation.origin_project_number
            ? `案件 ${quotation.origin_project_number} は残り、明細との結び付けだけが外れます。`
            : quotation.ordered_project_number
              ? `案件 ${quotation.ordered_project_number} を明細ごと削除します。`
              : "この見積から起票された案件はありません。"
        }\n見積の明細と図面は残り、状態は「提出済」に戻ります。\n\n出荷済みの案件がある場合は取り消せません。`}
        confirmLabel="受注を取り消す"
        isDanger
        isBusy={cancelOrder.isPending}
        onClose={() => setCancellingOrder(false)}
        onConfirm={() => cancelOrder.mutate()}
      />

      {togglingSetProduct != null && (
        <SetProductDialog
          owner="quotation"
          number={quotation.quotation_number}
          to={togglingSetProduct}
          previewKey={["quotation-set-product-preview", id, togglingSetProduct]}
          fetchPreview={() => previewQuotationSetProduct(id, togglingSetProduct)}
          onClose={() => setTogglingSetProduct(null)}
          onConfirm={async () => {
            await setQuotationSetProduct(id, togglingSetProduct);
            // 明細の行数が変わり、案件の明細も一緒に作り直される。
            await invalidate();
            await queryClient.invalidateQueries({ queryKey: ["projects"] });
            setTogglingSetProduct(null);
            showSuccess(
              togglingSetProduct ? "セット品にしました。" : "セット品を解除しました。",
            );
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deletingQuotation}
        title="見積の削除"
        message={`見積 ${quotation.quotation_number} を明細ごと削除します。\nこの操作は取り消せません。よろしいですか？\n\n案件に紐付いている見積は削除できません。`}
        confirmLabel="削除"
        isDanger
        isBusy={removeQuotation.isPending}
        onClose={() => setDeletingQuotation(false)}
        onConfirm={() => removeQuotation.mutate()}
      />
    </div>
  );
}
